import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideScoreCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Score CV : Comment l'Améliorer de 40 à 85 Points - Guide Complet 2025</title>
                <meta name="description" content="Découvrez comment améliorer votre score CV de 40 à 85 points : facteurs d'évaluation, actions concrètes par section, exemples de progression et checklist d'amélioration. Optimisez votre CV avec notre guide expert." />
                <meta name="keywords" content="score CV, améliorer score CV, score CV 40 50 60, comment augmenter score CV, évaluer CV, note CV, CV bien noté, amélioration score CV" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/score-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "Score CV : Comment l'Améliorer de 40 à 85 Points",
                  "description": "Guide complet pour améliorer votre score CV et optimiser votre candidature",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "datePublished": "2025-01-01",
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/score-cv"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment améliorer son score de CV",
                  "description": "Passez votre score CV de 40 à 85 points grâce à des actions concrètes sur la structure, les mots-clés et le contenu.",
                  "step": [
                    {"@type": "HowToStep", "name": "Comprendre les critères de scoring", "text": "Le score évalue structure, mots-clés, lisibilité, longueur et compatibilité ATS."},
                    {"@type": "HowToStep", "name": "Analyser la structure et le formatage", "text": "Vérifiez que toutes les sections essentielles sont présentes et bien organisées."},
                    {"@type": "HowToStep", "name": "Vérifier la densité de mots-clés", "text": "Assurez-vous que les mots-clés de l'offre sont bien présents dans votre CV."},
                    {"@type": "HowToStep", "name": "Enrichir le contenu avec des résultats chiffrés", "text": "Ajoutez des métriques concrètes à vos expériences : +30% de CA, 5 projets livrés, etc."},
                    {"@type": "HowToStep", "name": "Relancer l'analyse pour mesurer la progression", "text": "Retestez votre CV après chaque modification pour suivre l'évolution du score."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Score CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">📊</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Score CV : Comment l'Améliorer de 40 à 85 Points
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Découvrez les facteurs qui impactent votre score CV et les actions concrètes pour l'améliorer significativement
                        </p>
                    </div>
                </div>
            </div>

            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#fonctionnement" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">1</span>
                            <span className="text-gray-700">Comment fonctionne le scoring</span>
                        </a>
                        <a href="#facteurs" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">2</span>
                            <span className="text-gray-700">Facteurs d'évaluation</span>
                        </a>
                        <a href="#actions" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">3</span>
                            <span className="text-gray-700">Actions par section</span>
                        </a>
                        <a href="#progression" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">4</span>
                            <span className="text-gray-700">Exemples de progression</span>
                        </a>
                        <a href="#checklist" className="flex items-center p-3 rounded-xl hover:bg-green-50 transition-colors">
                            <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 text-green-600 font-bold">5</span>
                            <span className="text-gray-700">Checklist d'amélioration</span>
                        </a>
                    </div>
                </div>
            </div>

            <GuideLead slug="score-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div id="fonctionnement" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🔍</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Comment fonctionne le scoring CV</h2>
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            Le score CV est une évaluation automatisée qui analyse plusieurs critères pour déterminer la qualité et l'efficacité de votre CV. 
                            Un score élevé (80+) indique un CV optimisé qui a de fortes chances de passer les filtres ATS et d'attirer l'attention des recruteurs.
                        </p>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mt-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">📈 Échelles de score</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-green-600 font-bold mr-3">0-40 :</span>
                                    <span className="text-gray-700">CV nécessitant des améliorations majeures (structure, contenu, formatage)</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-yellow-600 font-bold mr-3">41-60 :</span>
                                    <span className="text-gray-700">CV correct mais perfectible (manque d'optimisation, erreurs mineures)</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-600 font-bold mr-3">61-75 :</span>
                                    <span className="text-gray-700">Bon CV (structure solide, contenu pertinent, quelques optimisations possibles)</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-600 font-bold mr-3">76-85 :</span>
                                    <span className="text-gray-700">Excellent CV (optimisé, bien structuré, contenu impactant)</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-emerald-600 font-bold mr-3">86+ :</span>
                                    <span className="text-gray-700">CV exceptionnel (parfaitement optimisé, toutes les bonnes pratiques appliquées)</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div id="facteurs" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚖️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Facteurs qui impactent le score</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-blue-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">📄 Structure et formatage (25%)</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Organisation claire des sections</li>
                                    <li>• Lisibilité et hiérarchie visuelle</li>
                                    <li>• Format compatible ATS</li>
                                    <li>• Longueur appropriée</li>
                                </ul>
                            </div>
                            <div className="bg-purple-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">✍️ Contenu et rédaction (30%)</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Pertinence des informations</li>
                                    <li>• Qualité des descriptions</li>
                                    <li>• Absence d'erreurs</li>
                                    <li>• Impact des formulations</li>
                                </ul>
                            </div>
                            <div className="bg-orange-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">🎯 Mots-clés et ATS (25%)</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Présence de mots-clés pertinents</li>
                                    <li>• Optimisation pour ATS</li>
                                    <li>• Alignement avec le poste</li>
                                    <li>• Compétences techniques</li>
                                </ul>
                            </div>
                            <div className="bg-green-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">✨ Valeur ajoutée (20%)</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Résultats chiffrés</li>
                                    <li>• Projets significatifs</li>
                                    <li>• Formations pertinentes</li>
                                    <li>• Différenciation</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="actions" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🚀</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Actions concrètes par section</h2>
                        </div>
                        <div className="space-y-6">
                            <div className="border-l-4 border-green-500 pl-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">1. En-tête et coordonnées</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>✅ Ajoutez un email professionnel</li>
                                    <li>✅ Incluez votre profil LinkedIn</li>
                                    <li>✅ Vérifiez que toutes les informations sont à jour</li>
                                    <li>✅ Utilisez un format cohérent</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-blue-500 pl-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">2. Profil/Objectif professionnel</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>✅ Rédigez un résumé percutant (3-4 lignes)</li>
                                    <li>✅ Mentionnez vos compétences clés</li>
                                    <li>✅ Adaptez-le au poste visé</li>
                                    <li>✅ Utilisez des mots-clés pertinents</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">3. Expériences professionnelles</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>✅ Utilisez des verbes d'action puissants</li>
                                    <li>✅ Quantifiez vos résultats (chiffres, pourcentages)</li>
                                    <li>✅ Décrivez vos responsabilités de manière impactante</li>
                                    <li>✅ Mettez en avant les réalisations, pas seulement les tâches</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-orange-500 pl-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">4. Compétences</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>✅ Listez les compétences techniques et soft skills</li>
                                    <li>✅ Alignez-les avec l'offre d'emploi</li>
                                    <li>✅ Organisez-les par catégories</li>
                                    <li>✅ Mentionnez les certifications pertinentes</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-teal-500 pl-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">5. Formations</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>✅ Mentionnez les diplômes obtenus</li>
                                    <li>✅ Ajoutez les formations continues pertinentes</li>
                                    <li>✅ Incluez les certifications professionnelles</li>
                                    <li>✅ Mettez en avant les formations récentes</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="progression" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📈</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Exemples de progression</h2>
                        </div>
                        <div className="space-y-6">
                            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Score 40 → 60 : Améliorations de base</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Corriger les erreurs d'orthographe et de grammaire</li>
                                    <li>• Restructurer le CV avec des sections claires</li>
                                    <li>• Ajouter un profil professionnel</li>
                                    <li>• Uniformiser le formatage</li>
                                    <li>• Compléter les informations manquantes</li>
                                </ul>
                            </div>
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Score 60 → 75 : Optimisation du contenu</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Quantifier les résultats et réalisations</li>
                                    <li>• Utiliser des verbes d'action puissants</li>
                                    <li>• Ajouter des mots-clés pertinents</li>
                                    <li>• Optimiser pour les ATS</li>
                                    <li>• Mettre en avant les projets significatifs</li>
                                </ul>
                            </div>
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">Score 75 → 85 : Excellence</h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li>• Personnaliser le CV pour chaque candidature</li>
                                    <li>• Mettre en avant des résultats exceptionnels</li>
                                    <li>• Optimiser chaque section au maximum</li>
                                    <li>• Ajouter des certifications et formations récentes</li>
                                    <li>• Créer un storytelling professionnel cohérent</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="checklist" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">✅</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Checklist d'amélioration</h2>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">CV structuré avec sections claires</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Aucune erreur d'orthographe</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Profil professionnel percutant</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Résultats chiffrés dans les expériences</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Mots-clés pertinents intégrés</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Format compatible ATS</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Longueur appropriée (1-2 pages)</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Compétences alignées avec le poste</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Formations et certifications à jour</span>
                                </div>
                                <div className="flex items-start">
                                    <input type="checkbox" className="mt-1 mr-3" />
                                    <span className="text-gray-700">Projets significatifs mentionnés</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">Quel score obtiendrait votre CV aujourd'hui ?</h2>
                    <p className="text-xl mb-6">Obtenez votre score détaillé sur 100 et les axes d'amélioration prioritaires — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-green-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Calculer mon score gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideScoreCV;








